import { useEffect, useRef, useState } from "react";
import ScrollRevealText from "./ScrollRevealText.jsx";
import Eyebrow from "./Eyebrow.jsx";
import "./Testimonial2.css";

// Swiper coverflow params used by the original section
const ROTATE = 50;
const STRETCH = 100;
const DEPTH = 100;
const AUTOPLAY = true;
const ROTATION_SPEED = 0.3; // 0.3 -> 1000 / 0.3 = 3333.33ms per rotation

/*
 * With two or fewer testimonials both are shown side by side as portrait cards (a coverflow with two
 * slides is mostly empty space). From three up the coverflow carousel below takes over, so adding
 * more later needs nothing but a new entry here. Photos live in public/assets/images/testimonials/.
 */
const DEFAULT_TESTIMONIALS = [
  {
    quote:
      "What impressed me most about Version3.ai is their strong understanding of how technology should work in the real world. They don’t just build features; they look at the underlying business process, customer experience, and measurable outcomes. Their approach shows the mindset of a team that understands both technology and business execution.",
    name: "Ziaul Haque",
    title: "Founder & MD",
    company: "Nexia Global Ventures Limited",
    photo: "/assets/images/testimonials/ziaul-haque-2.webp",
    photoSize: [760, 874],
    linkedin: "https://www.linkedin.com/in/ziaul-haque-342b36182/",
  },
  {
    quote:
      "Version3.ai is building with a rare combination of technical ambition and practical business thinking. The team has a clear understanding of where AI and automation can genuinely improve operations rather than using AI simply as a buzzword. I see strong potential in what they’re building for businesses in Bangladesh and beyond.",
    name: "Minhaz Us Salakeen Fahme",
    title: "Co-founder & CEO",
    company: "Battery Low Interactive Ltd.",
    photo: "/assets/images/testimonials/minhaz-fahme.webp",
    photoSize: [760, 839],
    linkedin: "https://www.linkedin.com/in/minhaz-fahme/",
  },
];

const LINKEDIN_PATH =
  "M5.40161 2.5C4.48974 2.5 3.75 3.23852 3.75 4.15039C3.75 5.06227 4.48914 5.81787 5.40039 5.81787C6.31164 5.81787 7.052 5.06227 7.052 4.15039C7.052 3.23914 6.31349 2.5 5.40161 2.5ZM13.4595 6.875C12.0726 6.875 11.2791 7.60028 10.8984 8.32153H10.8582V7.06909H8.125V16.25H10.9729V11.7053C10.9729 10.5078 11.0633 9.35059 12.5452 9.35059C14.0058 9.35059 14.0271 10.716 14.0271 11.781V16.25H16.8713H16.875V11.2073C16.875 8.73978 16.3445 6.875 13.4595 6.875ZM3.97705 7.06909V16.25H6.82739V7.06909H3.97705Z";

function Chevron() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 24 25" fill="none" aria-hidden="true">
      <path
        d="M15.75 12.5a.8.8 0 0 1-.22.55l-6 6.25a.73.73 0 0 1-1.06 0 .8.8 0 0 1 0-1.1l5.47-5.7-5.47-5.7a.8.8 0 0 1 0-1.1.73.73 0 0 1 1.06 0l6 6.25c.14.15.22.35.22.55Z"
        fill="currentColor"
      />
    </svg>
  );
}

const role = (t) => `${t.title}, ${t.company}`;

/* ------------------------------------------------------------------ */
/* Side by side: up to two testimonials                                */
/* ------------------------------------------------------------------ */

function Showcase({ items }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  // the cards come in once, when the row is about a third of the way up the screen
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return setSeen(true);
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setSeen(true);
        io.disconnect();
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <ul ref={ref} className={`t2-duo${seen ? " is-in" : ""}`} aria-label="Client testimonials">
      {items.map((t, i) => (
        <li key={t.name} className="t2-duo-item" style={{ "--i": i }}>
          <figure className="t2-card t2-card--duo">
            <div className="t2-portrait">
              <img src={t.photo} alt={t.name} width={t.photoSize?.[0]} height={t.photoSize?.[1]} loading="lazy" draggable="false" />
            </div>
            <div className="t2-duo-body">
              <div>
                <svg className="t2-mark" viewBox="0 0 32 24" aria-hidden="true">
                  <path
                    d="M0 24V14.2C0 6.3 4.4 1.3 12 0l1.2 3.6C9 4.9 7 7.5 6.7 11H13v13H0Zm19 0V14.2C19 6.3 23.4 1.3 31 0l1.2 3.6C28 4.9 26 7.5 25.7 11H32v13H19Z"
                    fill="currentColor"
                  />
                </svg>
                <blockquote className="t2-quote">{t.quote}</blockquote>
              </div>
              <figcaption className="t2-author t2-author--duo">
                <span className="t2-author-text">
                  <span className="t2-name">{t.name}</span>
                  <span className="t2-role">{role(t)}</span>
                </span>
                {t.linkedin && (
                  <a className="t2-linkedin" href={t.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${t.name} on LinkedIn`}>
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <path d={LINKEDIN_PATH} fill="currentColor" />
                    </svg>
                  </a>
                )}
              </figcaption>
            </div>
          </figure>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ */
/* Coverflow carousel: three or more                                    */
/* ------------------------------------------------------------------ */

// Swiper breakpoints: 2 per view >= 640, 1 per view >= 480, 80% width below
function slideWidthFor(width) {
  if (width >= 640) return width / 2;
  if (width >= 480) return width;
  return width * 0.8;
}

function Carousel({ testimonials }) {
  const viewportRef = useRef(null);
  const dragRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [ready, setReady] = useState(false);
  const [index, setIndex] = useState(() =>
    typeof window !== "undefined" && window.innerWidth >= 640 ? 1 : 0
  );
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  const count = testimonials.length;
  const last = count - 1;

  useEffect(() => {
    const el = viewportRef.current;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // enable transitions only after the first measured layout, so the
  // initial positioning doesn't animate in from zero
  useEffect(() => {
    if (!width || ready) return;
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, [width, ready]);

  const go = (next) => setIndex(Math.max(0, Math.min(last, next)));
  // the arrows and keys wrap round like the autoplay does (dragging still stops at the ends)
  const step = (delta) => setIndex((current) => (current + delta + count) % count);

  useEffect(() => {
    if (!AUTOPLAY || count < 2) return undefined;
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, 1000 / ROTATION_SPEED);
    return () => window.clearInterval(id);
  }, [count]);

  const slideW = width ? slideWidthFor(width) : 0;

  // fractional position of the centred slide (follows the finger while dragging)
  let pos = index - (slideW ? dragOffset / slideW : 0);
  // edge resistance, like Swiper's resistanceRatio
  if (pos < 0) pos = -Math.pow(-pos, 0.85) * 0.5;
  if (pos > last) pos = last + Math.pow(pos - last, 0.85) * 0.5;

  const trackX = width / 2 - slideW / 2 - pos * slideW;

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { x: e.clientX, t: performance.now(), id: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    setDragOffset(e.clientX - d.x);
  };

  const onPointerUp = (e) => {
    const d = dragRef.current;
    if (!d || d.id !== e.pointerId) return;
    dragRef.current = null;
    const dx = e.clientX - d.x;
    const quick = performance.now() - d.t < 300;
    if (quick && Math.abs(dx) > 10) {
      go(index - Math.sign(dx));
    } else if (slideW) {
      go(Math.round(index - dx / slideW));
    }
    setDragOffset(0);
    setDragging(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") step(1);
    if (e.key === "ArrowLeft") step(-1);
  };

  return (
    <div className="t2-stage" aria-roledescription="carousel">
      <div className="t2-arrows">
        <button
          type="button"
          className={`t2-arrow t2-arrow-back${index > 0 ? " is-shown" : ""}`}
          onClick={() => go(index - 1)}
          aria-label="Previous testimonial"
          tabIndex={index > 0 ? 0 : -1}
        >
          <span className="t2-arrow-icon">
            <Chevron />
          </span>
        </button>
        <button type="button" className="t2-arrow t2-arrow-next" onClick={() => step(1)} aria-label="Next testimonial">
          <span className="t2-arrow-icon">
            <Chevron />
          </span>
        </button>
      </div>

      <div
        ref={viewportRef}
        className={`t2-viewport${ready ? " is-ready" : ""}${dragging ? " is-dragging" : ""}`}
        tabIndex={0}
        aria-label="Testimonials, use arrow keys to navigate"
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="t2-track" style={{ transform: `translate3d(${trackX}px, 0, 0)` }}>
          {testimonials.map((t, i) => {
            const d = pos - i; // Swiper's offsetMultiplier: +1 for the slide on the left
            return (
              <div
                key={t.name}
                className="t2-slide"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${count}`}
                aria-hidden={i !== index}
                style={{
                  width: slideW || undefined,
                  zIndex: count - Math.abs(Math.round(d)),
                  transform: `translate3d(${d * STRETCH}px, 0, ${-DEPTH * Math.abs(d)}px) rotateY(${d * ROTATE}deg)`,
                }}
              >
                <figure className="t2-card">
                  <blockquote className="t2-quote">{t.quote}</blockquote>
                  <figcaption className="t2-author">
                    <span className="t2-avatar">
                      <img src={t.photo} alt="" loading="lazy" draggable="false" />
                    </span>
                    <span className="t2-author-text">
                      <span>{t.name}</span>
                      <span className="t2-role">{role(t)}</span>
                    </span>
                  </figcaption>
                </figure>
              </div>
            );
          })}
        </div>
      </div>

      <div className="t2-progress-wrap">
        <div className="t2-progress">
          <div className="t2-progress-fill" style={{ width: `${last ? (index / last) * 100 : 100}%` }} />
        </div>
      </div>
    </div>
  );
}

export default function Testimonial2({
  heading = "What founders say about Version3",
  testimonials = DEFAULT_TESTIMONIALS,
}) {
  return (
    <section className="t2" id="testimonials">
      <div className="t2-head">
        <div className="t2-head-inner">
          <Eyebrow section="testimonials" />
          <ScrollRevealText
            as="h2"
            className="t2-heading"
            text={heading}
            unit="word"
            minOpacity={0.1}
            startAt={0.8}
            endAt={0.3}
            endAnchor={0.5}
          />
        </div>
      </div>

      {testimonials.length > 2 ? <Carousel testimonials={testimonials} /> : <Showcase items={testimonials} />}
    </section>
  );
}
